import { useQuery } from "@tanstack/react-query";

import { api } from "../../api/client";
import type { ExportCompleteness } from "../../api/types";
import { Callout } from "../../components/Callout";
import { Presence } from "../../components/presence";
import { t } from "../../i18n/i18n";

/** Ce qu'efface la suppression d'un dossier et ce que le fichier « .generex » téléchargé d'abord en garde (audit
 *  M11), commun à l'accueil et au résumé du dossier. Le fichier contient tout, sauf les rôles des postes dans la
 *  DIREX ; au-delà des plafonds du fichier (journal très long, fichiers d'origine trop lourds pour un fichier
 *  réimportable), ce qui manquerait est dit ici, avant d'effacer quoi que ce soit. */
export function DeletionContents({ exerciseId }: { exerciseId: string }) {
  const { data } = useQuery({
    queryKey: ["export-completeness", exerciseId],
    queryFn: () => api.exportCompleteness(exerciseId),
    staleTime: 0,
    retry: 1,
  });
  const missing = data && !data.complete ? missingItems(data) : [];
  return (
    <>
      <p>
        {t(
          "Seront définitivement effacés du serveur : Données initiales, dispositif et graphiques, ordres et leurs versions, mise en ambiance, DIREX (incidents, journal, horloge, points des cellules), 3A, logistique, ordres reçus avec leurs fichiers et RETEX.",
        )}
      </p>
      <Presence show={Boolean(data?.complete)}>
        {data?.complete && (
          <p className="mt-2 text-ink-muted">
            {t(
              "Le fichier téléchargé d'abord contient tout le dossier, sauf les rôles des postes dans la DIREX ; un code de transmission en attente ne pourra plus être reçu.",
            )}
          </p>
        )}
      </Presence>
      <Presence show={missing.length > 0}>
        {missing.length > 0 && (
          <Callout tone="warn" className="mt-2" title={t("Le fichier téléchargé d'abord ne contiendra pas tout")}>
            <ul className="list-disc pl-4">
              {missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-1">
              {t("Ces éléments seront perdus à la suppression ; un code de transmission en attente ne pourra plus être reçu.")}
            </p>
          </Callout>
        )}
      </Presence>
    </>
  );
}

/** Éléments que le fichier laisserait de côté, en clair. */
export function missingItems(data: ExportCompleteness): string[] {
  const items: string[] = [];
  if (data.too_large) items.push(t("le fichier dépasse 64 Mo : il ne pourra pas être réimporté tel quel"));
  if (data.journal_left_out === 1) items.push(t("la ligne la plus ancienne du journal de la DIREX"));
  else if (data.journal_left_out > 1)
    items.push(t("les {count} lignes les plus anciennes du journal de la DIREX", { count: data.journal_left_out }));
  if (data.originals_left_out.length > 0)
    items.push(
      t("le fichier d'origine des ordres reçus {names} (leur texte est conservé)", {
        names: data.originals_left_out.map((name) => t("« {name} »", { name })).join(", "),
      }),
    );
  return items;
}
